import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  BFF_AUTH_ERROR_HEADER,
  BFF_RECOVERABLE_AUTH_ERRORS,
} from '../constants/bff-auth.constants';
import { environment } from '../environments/environment';
import { ScitBffHandshakeService } from '../services/viewer/scit-bff-handshake.service';

/**
 * Re-establishes the BFF session cookie when a credentialed SCIT → BFF call returns 401
 * with a recoverable X-BFF-Auth-Error, then retries the request once.
 */
@Injectable()
export class BffSessionRefreshInterceptor implements HttpInterceptor {
  private handshake = inject(ScitBffHandshakeService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const bffBaseUrl = environment.mayaViewer?.bffBaseUrl;
    if (!bffBaseUrl || !req.url.startsWith(bffBaseUrl)) {
      return next.handle(req);
    }

    if (req.url.includes('/bff/handshake')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (!this.isRecoverableBffAuthError(error)) {
          return throwError(() => error);
        }

        return this.handshake.ensureSession().pipe(
          switchMap((ok) => {
            if (!ok) {
              return throwError(() => error);
            }
            return next.handle(req);
          })
        );
      })
    );
  }

  private isRecoverableBffAuthError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
      return false;
    }

    const reason = error.headers.get(BFF_AUTH_ERROR_HEADER);
    return (
      reason !== null &&
      BFF_RECOVERABLE_AUTH_ERRORS.includes(
        reason as (typeof BFF_RECOVERABLE_AUTH_ERRORS)[number]
      )
    );
  }
}
